import React, { useCallback, useRef, useState, memo } from 'react';
import { Menu, MenuItem } from '@material-ui/core';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { BiDotsVerticalRounded, BiMenu, BiX } from 'react-icons/bi';
import { IoIosWarning } from 'react-icons/io';

import Toggle from '../Toggle';
import Button from '../Button';
import { ModalHandles } from '../Modal';
import { Product } from '../SimpleGroupAccordion';

import {
  TableRow,
  TableCell,
  ProductDetails,
  ActionButton,
  Modal,
  ModalHeader,
  ProductImage,
} from './styles';

import PlaceholderProductImage from '../../assets/no_image.png';
import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

type SimpleProductRowProps = {
  product: Product;
  isLoading: boolean;
  isExpanded: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  onEdit(): void;
  onClone(): void;
  onDelete(): void;
  onEditImage(): void;
  onChangePauseSale(): void;
};

const SimpleProductRow: React.FC<SimpleProductRowProps> = ({
  product,
  isLoading,
  isExpanded,
  dragHandleProps,
  onEdit,
  onClone,
  onDelete,
  onEditImage,
  onChangePauseSale,
}) => {
  const modalRef = useRef<ModalHandles>(null);
  const optionsButtonRef = useRef<HTMLButtonElement>(null);
  const { addToast } = useToast();
  const { findRotinas } = useAuth();

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  function handleSimpleProductPrice(): string {
    if (product?.price?.value) {
      return Number(product?.price.value).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      });
    }

    return 'Sem preço';
  }

  function handleSimpleProductStock(): string {
    if (product?.stock) {
      if (
        product?.stock?.currentStock !== undefined &&
        !Number.isNaN(product?.stock?.currentStock)
      ) {
        const currentStock = Number(product?.stock.currentStock);

        return `${currentStock} ${product.unitOfMeasurement.acronym}`;
      }
    }

    return 'Sem estoque';
  }

  const handleOpenOptions = useCallback(() => {
    setIsOptionsOpen(true);
  }, []);

  const handleCloseOptions = useCallback(() => {
    setIsOptionsOpen(false);
  }, []);

  const handleAttemptToDeleteProduct = useCallback(() => {
    modalRef.current?.open();
    handleCloseOptions();
  }, [handleCloseOptions]);

  const handleEditProduct = useCallback(() => {
    handleCloseOptions();
    onEdit();
  }, [onEdit, handleCloseOptions]);

  const handleCloneProduct = useCallback(() => {
    handleCloseOptions();
    onClone();
  }, [onClone, handleCloseOptions]);

  const handleDeleteProduct = useCallback(() => {
    handleCloseOptions();
    onDelete();
  }, [onDelete, handleCloseOptions]);

  const hasPermission = useCallback(
    (): boolean => findRotinas('grupos')?.alterar || false,
    [findRotinas],
  );

  return (
    <TableRow>
      <TableCell>
        <ProductDetails>
          <button type="button" disabled={isLoading} {...dragHandleProps}>
            <BiMenu />
          </button>
          <img
            alt={product.name}
            src={
              product.image
                ? `${process.env.REACT_APP_SERVER_URL}/files/${product.image}`
                : PlaceholderProductImage
            }
            style={{ width: 50, height: 50 }}
          />
          {/* <ProductImage
            placeholder={PlaceholderProductImage}
            url={`${process.env.REACT_APP_SERVER_URL}/files/${product.image}`}
            onClick={onEditImage}
          >
            <div />
          </ProductImage> */}
          <p>{product.name}</p>
        </ProductDetails>
      </TableCell>
      <TableCell>
        <p>{product?.code || 'Sem código'}</p>
      </TableCell>
      <TableCell>
        <p>{handleSimpleProductStock()}</p>
      </TableCell>
      <TableCell>
        <p>{handleSimpleProductPrice()}</p>
      </TableCell>
      <TableCell>
        <Toggle
          checked={!product.pauseSale}
          firstText="Pausado"
          secondText="Ativado"
          onToggle={() => {
            if (!hasPermission()) {
              addToast({
                type: 'error',
                title: 'Não autorizado.',
                description: 'Você não tem permissão.',
              });
              return;
            }
            onChangePauseSale();
          }}
        />
      </TableCell>
      <TableCell>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={handleEditProduct}
            style={{
              width: 36,
              minWidth: 36,
              height: 36,
              minHeight: 36,
              borderRadius: 5,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(50, 155, 252, 0.1)',
            }}
          >
            <span
              style={{ color: '#329BFC', fontSize: '18px' }}
              className="material-icons-outlined"
            >
              edit
            </span>
          </button>
          <button
            type="button"
            onClick={handleCloneProduct}
            style={{
              width: 36,
              minWidth: 36,
              height: 36,
              minHeight: 36,
              borderRadius: 5,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 162, 73, 0.1)',
            }}
          >
            <span
              style={{ color: '#00a249', fontSize: '18px' }}
              className="material-icons-outlined"
            >
              content_copy
            </span>
          </button>
          <button
            type="button"
            onClick={handleAttemptToDeleteProduct}
            style={{
              width: 36,
              minWidth: 36,
              height: 36,
              minHeight: 36,
              borderRadius: 5,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(246, 68, 68, 0.1)',
            }}
          >
            <span
              style={{ color: '#F64444', fontSize: '18px' }}
              className="material-icons-outlined"
            >
              delete
            </span>
          </button>
        </div>
        {/* {hasPermission() && (
          <>
            <ActionButton
              isExpanded={isExpanded}
              ref={optionsButtonRef}
              type="button"
              onClick={handleOpenOptions}
            >
              <BiDotsVerticalRounded />
            </ActionButton>
            <Menu
              id={`simple-product-${product.id}-options-menu`}
              anchorEl={optionsButtonRef.current}
              keepMounted
              open={isOptionsOpen}
              onClose={handleCloseOptions}
              PaperProps={{
                style: {
                  maxHeight: 48 * 4.5,
                  width: '20ch',
                },
              }}
            >
              <MenuItem onClick={handleEditProduct}>Editar</MenuItem>
              <MenuItem onClick={handleCloneProduct}>Clonar</MenuItem>
              <MenuItem onClick={handleAttemptToDeleteProduct}>
                Excluir
              </MenuItem>
            </Menu>
          </>
        )} */}
      </TableCell>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Ao realizar a ação todos os dados do produto &quot;{product.name}
          &quot; e seus complementos serão excluídos.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={handleDeleteProduct}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
    </TableRow>
  );
};

export default memo(SimpleProductRow);
